'use client';
import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import styles from './HomeCarousel.module.css';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import type { CarouselSlide } from '../../types/home.types';
import { BasePrimaryButton } from '@practium/ui';
import { CampaignOutlined } from '@mui/icons-material';

interface HomeCarouselProps {
	slides: CarouselSlide[];
	onSlideClick?: (link: string) => void;
}

export const HomeCarousel = ({ slides, onSlideClick }: HomeCarouselProps) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const isEmpty = !slides || slides.length === 0;

	const handleSlideChange = (swiper: SwiperType) => {
		setActiveIndex(swiper.realIndex);
	};

	const handleButtonClick = (link: string) => {
		if (onSlideClick) {
			onSlideClick(link);
		}
	};

	if (isEmpty) {
		return (
			<div className={`${styles.carouselContainer} ${styles.emptyContainer}`}>
				<div className={styles.emptyState}>
					<CampaignOutlined className={styles.emptyIcon} />
					<span className={styles.emptyTitle}>Nenhum comunicado disponível</span>
					<span className={styles.emptyDescription}>Novidades e avisos aparecerão aqui</span>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.carouselContainer}>
			<Swiper
				modules={[Pagination, Autoplay, Navigation]}
				spaceBetween={0}
				slidesPerView={1}
				autoplay={{
					delay: 5000,
					disableOnInteraction: false,
				}}
				loop={true}
				onSlideChange={handleSlideChange}
				className={styles.swiper}
			>
				{slides.map((slide) => (
					<SwiperSlide key={slide.id}>
						<div
							className={styles.slide}
							style={{
								backgroundImage: slide.imageUrl ? `url(${slide.imageUrl})` : undefined,
							}}
						>
							<div className={styles.slideContent}>
								<h2 className={styles.slideTitle}>{slide.title}</h2>
								<BasePrimaryButton
									onClick={() => handleButtonClick(slide.buttonLink)}
									className={styles.slideButton}
								>
									{slide.buttonText}
								</BasePrimaryButton>
							</div>
						</div>
					</SwiperSlide>
				))}
			</Swiper>

			<div className={styles.customPagination}>
				{slides.map((slide) => (
					<button
						key={slide.id}
						className={`${styles.paginationDot} ${
							slides.indexOf(slide) === activeIndex ? styles.paginationDotActive : ''
						}`}
						aria-label={`Ir para slide ${slides.indexOf(slide) + 1}`}
					/>
				))}
			</div>
		</div>
	);
};
